/**
 * CreateCallScreen — Create a room and show QR for peer to scan.
 *
 * Shows QR code with room info. Both peers navigate to CallPage independently.
 * The creator auto-navigates to CallPage after a short delay to show the QR.
 * The joiner navigates after scanning.
 * Both meet in CallPage where WebRTCService.start() handles the connection.
 */

import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useWallet } from '../wallet/WalletContext.js';
import { generateQrDataUrl } from './qrService.js';
import { encodePairingPayload, buildJoinUrl, type PairingPayload } from './pairingPayload.js';

interface Props {
  signalingUrl: string;
  chain: string;
  onReady: (roomId: string, secret: string) => void;
  onCancel: () => void;
}

type CreateState = 'creating' | 'showing' | 'error';

export function CreateCallScreen({ signalingUrl, chain, onReady, onCancel }: Props): ReactNode {
  const { status } = useWallet();
  const [state, setState] = useState<CreateState>('creating');
  const [qrUrl, setQrUrl] = useState('');
  const [joinLink, setJoinLink] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [roomData, setRoomData] = useState<{ roomId: string; secret: string } | null>(null);
  const [peerJoined, setPeerJoined] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const autoConnectedRef = useRef(false);

  const callerAddress = status.state === 'unlocked' ? status.address : '';

  useEffect(() => {
    void createRoom();
    return () => {
      // Cleanup signaling websocket if component unmounts
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, []);

  const createRoom = async () => {
    setState('creating');
    try {
      const apiBase = `${window.location.origin}/api`;
      const response = await fetch(`${apiBase}/rooms`, { method: 'POST' });
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as Record<string, string>;
        throw new Error(body['message'] ?? `Server error: ${response.status}`);
      }

      const data = await response.json() as { roomId: string; secret: string };
      setRoomData(data);

      // Connect to signaling immediately to detect when peer joins
      const wsUrl = signalingUrl + `?roomId=${data.roomId}&secret=${data.secret}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type === 'peer-joined') {
            setPeerJoined(true);
          }
        } catch { /* ignore non-JSON */ }
      };
      ws.onerror = () => { /* signaling error is non-fatal here */ };

      const payload: PairingPayload = {
        signalingUrl,
        roomId: data.roomId,
        secret: data.secret,
        callerAddress,
        chain,
        timestamp: Date.now(),
      };

      const joinUrl = buildJoinUrl(window.location.origin, payload);
      setJoinLink(joinUrl);
      const qr = await generateQrDataUrl(joinUrl, 300);
      setQrUrl(qr);
      setState('showing');
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Failed to create room');
    }
  };

  // Auto-navigate to call when peer joins
  useEffect(() => {
    if (peerJoined && roomData && !autoConnectedRef.current) {
      autoConnectedRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
      onReady(roomData.roomId, roomData.secret);
    }
  }, [peerJoined, roomData, onReady]);

  const handleCopyLink = useCallback(() => {
    void navigator.clipboard.writeText(joinLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [joinLink]);

  const handleConnect = useCallback(() => {
    if (roomData) {
      onReady(roomData.roomId, roomData.secret);
    }
  }, [roomData, onReady]);

  if (state === 'creating') {
    return (
      <div className="screen"><div className="screen-body centered">
        <div className="loading-spinner" /><p>Creating room...</p>
      </div></div>
    );
  }

  if (state === 'error') {
    return (
      <div className="screen"><div className="screen-body centered">
        <div className="error-icon">!</div>
        <h3>Error</h3>
        <p className="error-message">{error}</p>
        <button className="btn-primary" onClick={() => void createRoom()}>Try Again</button>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div></div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
        <h2>Start Streaming</h2>
      </div>
      <div className="screen-body centered">
        <p className="text-secondary">
          Show this QR to your peer. They scan it, connection starts automatically.
        </p>
        <div className="qr-container qr-large">
          {qrUrl ? <img src={qrUrl} alt="QR" className="qr-image" /> : <div className="qr-placeholder">Generating...</div>}
        </div>
        <div className="link-share">
          <input type="text" readOnly value={joinLink} className="link-input" onClick={e => (e.target as HTMLInputElement).select()} />
          <button className="btn-secondary btn-sm" onClick={handleCopyLink}>{copied ? 'Copied!' : 'Copy'}</button>
        </div>
        <button className="btn-primary btn-full" onClick={handleConnect}>
          {peerJoined ? 'Connecting...' : 'Waiting for peer...'}
        </button>
      </div>
    </div>
  );
}
